import React from 'react';

const styles = {
    page: {
        backgroundColor: '#25412E',
        minHeight: '100vh',
        padding: '60px 20px',
        fontFamily: "'Georgia', serif",
    },
    container: {
        maxWidth: '760px',
        margin: '40px auto',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '60px 56px',
        boxShadow: '0 2px 24px rgba(26,18,9,0.07)',
    },
    header: {
        marginBottom: '40px',
    },
    eyebrow: {
        fontSize: '11px',
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: '#C19A6B',
        marginBottom: '12px',
    },
    heading: {
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        fontWeight: 400,
        color: '#1A1209',
        lineHeight: 1.2,
        margin: '0 0 16px',
    },
    meta: {
        fontSize: '14px',
        fontFamily: "'Inter', sans-serif",
        color: '#6B5E4E',
        lineHeight: 1.7,
        margin: '0 0 8px',
    },
    updated: {
        fontSize: '12px',
        fontFamily: "'Inter', sans-serif",
        color: '#9E8E7E',
        margin: 0,
    },
    divider: {
        border: 'none',
        borderTop: '1px solid #EDE8E0',
        margin: '40px 0',
    },
    section: {
        marginBottom: '36px',
    },
    sectionHeading: {
        fontSize: '18px',
        fontWeight: 600,
        fontFamily: "'Inter', sans-serif",
        color: '#1A1209',
        marginBottom: '12px',
        display: 'flex',
        gap: '8px',
        alignItems: 'baseline',
    },
    sectionNumber: {
        color: '#C19A6B',
    },
    para: {
        fontSize: '15px',
        fontFamily: "'Inter', sans-serif",
        color: '#4A3F35',
        lineHeight: 1.8,
        margin: '0 0 12px',
    },
    subLabel: {
        fontSize: '13px',
        fontFamily: "'Inter', sans-serif",
        fontWeight: 600,
        color: '#1A1209',
        letterSpacing: '0.04em',
        margin: '16px 0 6px',
    },
    list: {
        margin: '0 0 12px',
        paddingLeft: '20px',
    },
    listItem: {
        fontSize: '15px',
        fontFamily: "'Inter', sans-serif",
        color: '#4A3F35',
        lineHeight: 1.8,
        marginBottom: '4px',
    },
    footerNote: {
        fontSize: '13px',
        fontFamily: "'Inter', sans-serif",
        color: '#9E8E7E',
        fontStyle: 'italic',
        textAlign: 'center',
        margin: 0,
    },
};

const p = (text) => <p style={styles.para}>{text}</p>;
const ul = (items) => (
    <ul style={styles.list}>
        {items.map((item, i) => <li key={i} style={styles.listItem}>{item}</li>)}
    </ul>
);

const SECTIONS = [
    {
        title: 'Who We Are',
        content: p('Berli Brewing Co. is a craft brewery and taproom operated by Tiny Rebel Brewing Private Limited, located in Kharadi, Pune, Maharashtra, India. This Privacy Policy explains how we collect, use, store, and protect your personal information when you interact with us through our website, reservation system, or contact form.'),
    },
    {
        title: 'Information We Collect',
        content: (
            <>
                <p style={styles.subLabel}>Through our Reservation Form:</p>
                {ul(['Full name', 'Phone number', 'Email address', 'Preferred date, time slot, and number of guests', 'Occasion and special requests'])}
                <p style={styles.subLabel}>Through our Contact Form:</p>
                {ul(['Full name', 'Email address', 'Type of enquiry', 'Message content'])}
                <p style={styles.subLabel}>Automatically:</p>
                {ul(['Browser type and device information', 'Pages visited and time spent on the site', 'IP address (via standard web hosting logs)'])}
            </>
        ),
    },
    {
        title: 'How We Use Your Information',
        content: (
            <>
                {p('We use the information you provide to:')}
                {ul([
                    'Confirm and manage your table reservations',
                    'Respond to your enquiries and messages',
                    'Improve our services and guest experience',
                    'Send reservation confirmations via phone or email',
                    'Maintain internal records for operational purposes',
                ])}
                {p('We do not use your data for automated decision-making or profiling.')}
            </>
        ),
    },
    {
        title: 'How We Store Your Information',
        content: (
            <>
                {p('Your data is stored securely using:')}
                {ul([
                    'Firebase Firestore — a secure cloud database by Google',
                    'Google Sheets — used internally by our team to manage reservations and enquiries',
                ])}
                {p('Both platforms are hosted on Google\'s infrastructure and comply with standard data protection practices. Access is restricted to authorised members of the Berli Brewing Co. team only.')}
            </>
        ),
    },
    {
        title: 'Who We Share Your Data With',
        content: (
            <>
                {p('We do not sell, rent, or trade your personal information to any third party.')}
                {p('Your data may be accessed by:')}
                {ul([
                    'Internal team members managing reservations and enquiries',
                    'Google (as the infrastructure provider for Firebase and Sheets)',
                ])}
                {p('We do not share your data with marketing agencies, advertisers, or any external parties for commercial purposes.')}
            </>
        ),
    },
    {
        title: 'How Long We Keep Your Data',
        content: p('We retain your personal data for as long as necessary to fulfil the purpose for which it was collected — typically no longer than 12 months from the date of submission. After this period, data is reviewed and deleted unless required for legal or operational reasons.'),
    },
    {
        title: 'Your Rights',
        content: (
            <>
                {p('You have the right to:')}
                {ul([
                    'Access the personal data we hold about you',
                    'Correct any inaccurate or incomplete information',
                    'Request deletion of your personal data',
                    'Withdraw consent at any time for any data processing based on consent',
                ])}
                {p('To exercise any of these rights, please contact us at contact@berlibrewing.com.in. We will respond to all requests within 30 days.')}
            </>
        ),
    },
    {
        title: 'Cookies',
        content: p('Our website may use basic cookies to ensure the site functions correctly. We do not use cookies for advertising or cross-site tracking. You can disable cookies in your browser settings at any time without affecting your ability to use the site.'),
    },
    {
        title: 'Third-Party Links',
        content: p('Our website may contain links to third-party platforms such as Instagram, Facebook, and Google Maps. We are not responsible for the privacy practices of these platforms. We encourage you to review their respective privacy policies.'),
    },
    {
        title: "Children's Privacy",
        content: p('Our website and services are not directed at individuals under the age of 18. We do not knowingly collect personal data from minors. If you believe a minor has submitted data through our site, please contact us and we will delete it promptly.'),
    },
    {
        title: 'Changes to This Policy',
        content: p('We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated date at the top. We encourage you to review this page periodically.'),
    },
    {
        title: 'Contact Us',
        content: (
            <>
                {p('If you have any questions about this Privacy Policy or how we handle your data, please reach out:')}
                {ul([
                    'Tiny Rebel Brewing Private Limited',
                    'Kharadi, Pune, Maharashtra, India',
                    'Email: contact@berlibrewing.com',
                    'Phone: +91 74200 01640',
                    'Instagram: @berlibrewingco',
                ])}
            </>
        ),
    },
];

const PrivacyPolicy = () => {
    return (
        <div style={styles.page}>
            <div style={styles.container}>

                <div style={styles.header}>
                    <p style={styles.eyebrow}>Berli Brewing Co.</p>
                    <h1 style={styles.heading}>Privacy Policy</h1>
                    <p style={styles.meta}>
                        Tiny Rebel Brewing Pvt. Ltd. · Kharadi, Pune, Maharashtra, India<br />
                        contact@berlibrewing.com · +91 74200 01640
                    </p>
                    <p style={styles.updated}>Last updated: June 2026</p>
                </div>

                <hr style={styles.divider} />

                {SECTIONS.map((section, i) => (
                    <div key={i} style={styles.section}>
                        <h2 style={styles.sectionHeading}>
                            <span style={styles.sectionNumber}>{i + 1}.</span> {section.title}
                        </h2>
                        {section.content}
                    </div>
                ))}

                <hr style={styles.divider} />

                <p style={styles.footerNote}>
                    By using our website and submitting your information, you agree to the terms of this Privacy Policy.
                </p>

            </div>
        </div>
    );
};

export default PrivacyPolicy;